import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import styles from './ForgotPassword.module.css';
import { Button, LabeledFormField, Notify } from 'components/core';
import { RouteConstants } from '../../../constants';
import { usePasswordService } from 'services';
import { ApiError } from 'helpers';

interface IFormValues {
  email: string;
}

export const ForgotPassword = () => {
  const { t } = useTranslation();
  const passwordService = usePasswordService();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validationSchema = Yup.object().shape({
    email: Yup.string().required(t('AUTH.EMAIL_REQUIRED')).email(t('AUTH.EMAIL_INVALID')),
  });

  const { register, handleSubmit, formState } = useForm<IFormValues>({
    resolver: yupResolver(validationSchema),
  });
  const { errors, isSubmitting } = formState;

  const onSubmit = (values: IFormValues) => {
    return passwordService
      .forgotPassword(values.email)
      .then(() => setIsSubmitted(true))
      .catch((message: ApiError) => Notify.error(message || t('AUTH.FORGOT_PASSWORD_FAILED_FALLBACK')));
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {isSubmitted ? (
          <>
            <h1 className={`h4 ${styles.title}`}>{t('AUTH.FORGOT_PASSWORD_SUCCESS_TITLE')}</h1>
            <div className={styles.confirmationBox}>
              <span className={`material-icons ${styles.confirmationIcon}`}>
                mark_email_read
              </span>
              <span>{t('AUTH.FORGOT_PASSWORD_SUCCESS_MESSAGE')}</span>
            </div>
          </>
        ) : (
          <>
            <h1 className={`h4 ${styles.title}`}>{t('AUTH.FORGOT_PASSWORD_TITLE')}</h1>
            <p className={styles.subtitle}>{t('AUTH.FORGOT_PASSWORD_SUBTITLE')}</p>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <LabeledFormField
                label={t('AUTH.EMAIL_LABEL')}
                icon="mail_outline"
                type="email"
                placeholder={t('AUTH.EMAIL_PLACEHOLDER')}
                required
                autoFocus
                error={errors.email?.message}
                registration={register('email')}
              />

              <Button className="w-100" disabled={isSubmitting}>
                {isSubmitting && <span className="spinner-border spinner-border-sm me-3"></span>}
                {isSubmitting ? t('AUTH.SENDING_RESET_LINK') : t('AUTH.SEND_RESET_LINK_BUTTON')}
              </Button>
            </form>
          </>
        )}

        <Link to={RouteConstants.login} className={styles.backToLogin}>
          {t('AUTH.BACK_TO_LOGIN')}
        </Link>
      </div>
    </div>
  );
};
